import type { UnlinkRequestItem } from "@services/facilityNetworkUnlink";
import { abbreviateFacilityType, registryIdOrNa } from "./facilityNetworkChildrenUtils";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { resolveFacilityRegistryDisplayIds, bareNpiFromCcn } from "@utils/facilityUtils";
import { expiryDaysFromCreatedAt } from "./parentLinkRequestUtils";

export type UnlinkRequestStatus = "approved" | "pending" | "rejected" | "cancelled" | "expired";

export type UnlinkRequestBannerData = {
  requestId: number;
  counterpartyFacilityName: string;
  npi?: string;
  ccn?: string;
  expiresInDays?: number;
  facilityType?: string;
  state?: string;
  facilityRole: "Parent facility" | "Child facility";
  reason?: string;
  initiatorRole: "parent" | "child";
};

export type UnlinkRequestDisplayRow = {
  id: number;
  facilityName: string;
  facilityRole: "Parent facility" | "Child facility";
  ccn: string;
  npi: string;
  typeLabel: string;
  requestedLabel: string;
  reason: string;
  status: UnlinkRequestStatus;
  resolutionDate: string | null;
  resolutionNote: string | null;
};

export function mapReceivedUnlinkRequestToBanner(
  item: UnlinkRequestItem,
): UnlinkRequestBannerData {
  const role =
    item.facility_role === "Parent facility" || item.facility_role === "Child facility"
      ? item.facility_role
      : item.initiator_role === "parent"
        ? "Parent facility"
        : "Child facility";

  const ids = resolveFacilityRegistryDisplayIds({
    npi_number: item.facility_npi,
    ccn_number: item.facility_ccn,
  });
  const npi = ids.npi || bareNpiFromCcn(item.facility_npi);

  return {
    requestId: item.id,
    counterpartyFacilityName: item.facility_name,
    npi: npi || undefined,
    ccn: ids.ccn || undefined,
    expiresInDays: expiryDaysFromCreatedAt(item.created_at) ?? undefined,
    facilityType: item.facility_type?.trim() || undefined,
    state: item.facility_state?.trim() || undefined,
    facilityRole: role,
    reason: item.reason?.trim() || undefined,
    initiatorRole: item.initiator_role,
  };
}

export function counterpartyDisplayName(request: UnlinkRequestBannerData): string {
  return request.counterpartyFacilityName;
}

/** AB#11673 — child has one parent; name that parent. Parent accept names the child. */
export function unlinkAcceptedSuccessMessage(input: {
  initiatorRole?: "parent" | "child" | null;
  counterpartyName?: string | null;
}): string {
  const name = (input.counterpartyName || "").trim();
  if (input.initiatorRole === "parent") {
    return name
      ? `You've been unlinked from ${name}.`
      : "You've been unlinked from your parent facility.";
  }
  return name ? `Unlinked from ${name}.` : "Facility unlinked.";
}

export function counterpartyTypeAbbrev(request: UnlinkRequestBannerData): string {
  if (!request.facilityType) return "—";
  const fakeFacility = {
    facility_type: request.facilityType,
    facility_type_slug: request.facilityType.toLowerCase().replace(/\s+/g, "_"),
  } as FacilityHierarchyInput;
  return abbreviateFacilityType(fakeFacility);
}

export function requestingFacilityLabel(request: UnlinkRequestBannerData): string {
  return request.facilityRole === "Parent facility"
    ? "Requesting parent facility"
    : "Requesting child facility";
}

export function mapUnlinkRequestRow(item: UnlinkRequestItem): UnlinkRequestDisplayRow {
  const role =
    item.facility_role === "Parent facility" || item.facility_role === "Child facility"
      ? item.facility_role
      : item.initiator_role === "parent"
        ? "Child facility"
        : "Parent facility";

  const ids = resolveFacilityRegistryDisplayIds({
    npi_number: item.facility_npi,
    ccn_number: item.facility_ccn,
  });
  const npi = ids.npi || bareNpiFromCcn(item.facility_npi);

  return {
    id: item.id,
    facilityName: item.facility_name,
    facilityRole: role,
    ccn: registryIdOrNa(ids.ccn),
    npi: registryIdOrNa(npi),
    typeLabel: item.facility_type?.trim() || "—",
    requestedLabel: formatUnlinkRequestedDate(item.created_at),
    reason: item.reason?.trim() || "—",
    status: item.status,
    resolutionDate: item.resolved_at ? formatUnlinkRequestedDate(item.resolved_at) : null,
    resolutionNote: item.resolution_note?.trim() || null,
  };
}

export function unlinkStatusLabel(status: UnlinkRequestStatus): string {
  switch (status) {
    case "approved":
      return "Approved";
    case "pending":
      return "Pending";
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function formatUnlinkRequestedDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
